import {
  Button,
  Drawer,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { ApprovalsService, CancelablePromise } from 'api-clients/monolith';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import globalMessages from 'shared/global/messages';

import { ConditionBuilder } from './ConditionBuilder';
import { CatalogState } from './conditionForm';
import messages from './messages';
import {
  draftFor,
  draftForNew,
  emptyLevel,
  hasErrors,
  levelHasError,
  LevelDraft,
  RuleDraft,
  RulePayload,
  toPayload,
  validate,
} from './ruleForm';
import { useStyles } from './styles';
import { ApprovalRule, OFFER, SubjectType } from './types';

export interface UserOption {
  external_id: string;
  name: string;
}

/** What a successful create returns; update returns the same rule shape. */
type SaveRuleResponse = Awaited<
  ReturnType<typeof ApprovalsService.postInternalApiApprovalsRules>
>;

export interface RuleEditorFormProps {
  /** The rule being edited; absent means this is a new rule. */
  rule?: ApprovalRule;
  /** Only used when creating — an existing rule's type is fixed. */
  subjectType?: SubjectType;
  userOptions: UserOption[];
  /**
   * The typed field catalog the condition builder is driven by (AXHE-3929),
   * carried as a state rather than an array so the builder can tell a failed
   * fetch from an account with nothing to gate on.
   */
  catalog: CatalogState;
  /** Retries the catalog fetch, offered when it failed. */
  onRetryCatalog: () => void;
  onClose: () => void;
  onSaved: () => void;
}

export interface RuleEditorPanelProps extends RuleEditorFormProps {
  open: boolean;
}

/**
 * Create and edit an approval rule.
 *
 * A right-hand slide-in rather than a centred modal: this is significant data
 * entry — a name and a variable number of levels each with their own approvers —
 * and a modal is the wrong container for that. It also lets the list stay
 * visible behind the panel, which is what makes "another rule already covers
 * this" noticeable while you are writing one.
 *
 * ## What this panel deliberately cannot switch on or off
 *
 * Neither the rule's `enabled` nor a level's (AXHE-4470). Design Requirements §2
 * locks "only whole rules have an enable/disable toggle. Individual levels
 * cannot be toggled on/off", and the mock puts the rule's one toggle in the
 * list row's Enabled column — not in here. Two switches shipped anyway during
 * the original transcription (AXHE-3945): a per-level "Level active" and a
 * second "Rule active" duplicating the row's.
 *
 * Both values are still *carried* — see `toPayload`. Dropping them would not be
 * a UI-only change: `SaveRule#create_level` defaults an absent `enabled` to
 * true, so a payload that stopped naming it would silently re-activate a level
 * an admin had deliberately switched off, and `RuleMatcher` would start routing
 * approvals through an approver they meant to bypass. So an already-off level
 * keeps its state and says so instead, since nothing can switch it back.
 *
 * Each level's data-key conditions are edited here via `ConditionBuilder`
 * (AXHE-3929). Everything else — a level's other condition types and the
 * rule's own scope conditions — is **not** edited here (AXHE-4107 owns that)
 * and is carried back untouched — see `toPayload`, where getting this wrong
 * would clear them.
 */
export const RuleEditorForm: React.FC<RuleEditorFormProps> = ({
  rule,
  subjectType = OFFER,
  userOptions,
  catalog,
  onRetryCatalog,
  onClose,
  onSaved,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const [draft, setDraft] = useState<RuleDraft>(() =>
    rule ? draftFor(rule) : draftForNew(subjectType),
  );
  // Validation runs on submit, not on keystroke — flagging a name as missing
  // while the user is still walking toward the field is premature.
  const [submitted, setSubmitted] = useState(false);

  // Undefined until the catalog resolves. Both `validate` and `toPayload` treat
  // that as "conditions were not editable this session", which is exactly true:
  // the builder renders its own loading and error states instead of a picker.
  const fieldCatalog = catalog.status === 'ready' ? catalog.catalog : undefined;

  /**
   * Mints the key for each level added here.
   *
   * A counter that only ever goes up, not `levels.length`: length is not
   * monotonic, so add → remove → add re-issued a key that was already on a card
   * (add to a one-level draft gives `level-new-1`, removing the other card puts
   * the length back to 1, and the next add gives `level-new-1` again).
   *
   * That matters more than a duplicate React key. Every mutation in this panel
   * routes on `level.key` — `updateLevel` patches *every* level whose key
   * matches and remove filters *every* level whose key matches — so two cards
   * sharing a key stop behaving as separate levels while `toPayload` still
   * submits both, and a rule can be saved whose second level holds approvers
   * the admin only entered once.
   *
   * A ref rather than state: it must survive a render without causing one, and
   * nothing renders from it. The `level-new-` prefix keeps these clear of the
   * `level-N` keys `draftFor` mints for persisted levels, so the counter only
   * has to be unique against itself and can start at zero.
   */
  const nextLevelKey = useRef(0);
  const mintLevelKey = () => {
    const key = `level-new-${nextLevelKey.current}`;
    nextLevelKey.current += 1;
    return key;
  };

  const errors = validate(draft, fieldCatalog);
  const showErrors = submitted && hasErrors(errors);

  // Both the service and the options must keep a stable identity across renders.
  // Passed inline they are new objects every time, and the hook's internals
  // re-derive from them — which turns each keystroke in the name field into
  // another render, and the form tears itself down once React gives up. The
  // sibling `useApiService` documents the same requirement in as many words.
  // Annotated rather than inferred: create and update declare their responses
  // separately in the OpenAPI document, so the ternary infers a union of two
  // structurally identical types and the mutation hook — which resolves a single
  // `T` — cannot match it. The panel only ever reads `result.status`, so the
  // narrower of the two is the honest common type.
  const saveRule = useCallback(
    (payload: RulePayload): CancelablePromise<SaveRuleResponse> =>
      rule
        ? ApprovalsService.patchInternalApiApprovalsRules(rule.id as string, payload)
        : ApprovalsService.postInternalApiApprovalsRules(payload),
    [rule],
  );
  const mutationOptions = useMemo(() => ({ onSuccess: onSaved }), [onSaved]);

  // Type arguments given rather than inferred: the hook's `T` only appears in the
  // service's return and in `onSuccess`, and this `onSuccess` takes no argument —
  // so there is nothing for TypeScript to infer `T` from and it lands on
  // `unknown`, which the annotated service then fails to satisfy.
  const { result, mutation } = useApiServiceMutation<SaveRuleResponse, typeof saveRule>(
    saveRule,
    mutationOptions,
  );

  const updateLevel = (key: string, patch: Partial<LevelDraft>) =>
    setDraft(current => ({
      ...current,
      levels: current.levels.map(level =>
        level.key === key ? { ...level, ...patch } : level,
      ),
    }));

  const handleSave = () => {
    setSubmitted(true);
    if (hasErrors(validate(draft, fieldCatalog))) return;

    mutation(toPayload(draft, fieldCatalog));
  };

  const title = () => {
    if (rule) return messages.editRuleTitle;
    return draft.subjectType === OFFER
      ? messages.newOfferRuleTitle
      : messages.newOpeningRuleTitle;
  };

  return (
    <>
      <div className={styles.panel} role="dialog" aria-label={intl.formatMessage(title())}>
        {/* h3, not h5: this theme's h5 is 12px, which would leave the panel's
            own title smaller than every line of copy beneath it. h3 is what the
            other recruiter-ui panels and modals use for the same job. */}
        <Typography variant="h3">
          <FormattedMessage {...title()} />
        </Typography>

        {result.status === 'error' && (
          <div className={styles.error} role="alert">
            <Typography variant="body1">
              <FormattedMessage {...messages.saveFailed} />
            </Typography>
          </div>
        )}

        <TextField
          id="approval-rule-name"
          className={styles.field}
          label={intl.formatMessage(messages.ruleNameLabel)}
          value={draft.name}
          onChange={event =>
            setDraft(current => ({ ...current, name: event.target.value }))
          }
          error={showErrors && errors.name === 'required'}
          helperText={
            showErrors && errors.name === 'required'
              ? intl.formatMessage(messages.ruleNameRequired)
              : undefined
          }
          fullWidth
        />

        {draft.levels.map((level, index) => (
          <div key={level.key} className={styles.levelCard} data-testid="level-card">
            <div className={styles.levelCardHeader}>
              <Typography variant="body2">
                <FormattedMessage {...messages.levelHeading} values={{ number: index + 1 }} />
              </Typography>

              {/* Destructive, so it is never primary weight — a text button at
                  the end of the header row, not a filled button next to Save. */}
              {draft.levels.length > 1 && (
                <IconButton
                  size="small"
                  aria-label={intl.formatMessage(messages.removeLevel)}
                  onClick={() =>
                    setDraft(current => ({
                      ...current,
                      levels: current.levels.filter(l => l.key !== level.key),
                    }))
                  }
                >
                  ×
                </IconButton>
              )}
            </div>

            <TextField
              id={`level-name-${level.key}`}
              className={styles.field}
              label={intl.formatMessage(messages.levelNameLabel)}
              value={level.name}
              onChange={event => updateLevel(level.key, { name: event.target.value })}
              fullWidth
            />

            <TextField
              id={`level-approvers-${level.key}`}
              className={styles.field}
              label={intl.formatMessage(messages.approversLabel)}
              value={level.approverIds}
              onChange={event =>
                updateLevel(level.key, {
                  approverIds: event.target.value as unknown as string[],
                })
              }
              error={showErrors && levelHasError(errors, level.key, 'no_approvers')}
              helperText={
                showErrors && levelHasError(errors, level.key, 'no_approvers')
                  ? intl.formatMessage(messages.approverRequired)
                  : undefined
              }
              SelectProps={{ multiple: true }}
              select
              fullWidth
            >
              {userOptions.map(user => (
                <MenuItem key={user.external_id} value={user.external_id}>
                  {user.name}
                </MenuItem>
              ))}
            </TextField>

            {/*
              The level lists approvers whose users have been offboarded. They
              cannot be sent back (the write contract needs a user_id the read
              API no longer has), so saving drops them — which matches the
              engine, where an offboarded approver already resolves to nobody.
              Saying so is the point: the same edit used to shrink the level in
              silence.
            */}
            {level.offboardedApproverCount > 0 && (
              <Typography
                variant="body2"
                className={styles.inlineWarning}
                role="status"
              >
                <FormattedMessage
                  {...messages.offboardedApprovers}
                  values={{ count: level.offboardedApproverCount }}
                />
              </Typography>
            )}

            <ConditionBuilder
              conditions={level.conditions}
              catalog={catalog}
              onRetryCatalog={onRetryCatalog}
              showErrors={showErrors}
              idPrefix={level.key}
              onChange={conditions => updateLevel(level.key, { conditions })}
              logic={level.conditionLogic}
              onLogicChange={conditionLogic =>
                updateLevel(level.key, { conditionLogic })
              }
            />

            {/*
              Only ever reached by a level switched off before AXHE-4470 removed
              the switch. Said out loud rather than left as an invisible
              property: the level lists approvers, sits in the sequence, and is
              skipped anyway, so an admin reading this card has no other way to
              tell why the rule never stops there. Read-only — this panel no
              longer owns the value, it only declines to overwrite it.
            */}
            {!level.enabled && (
              <Typography
                variant="body2"
                className={styles.inlineWarning}
                role="status"
              >
                <FormattedMessage {...messages.levelOff} />
              </Typography>
            )}
          </div>
        ))}

        <Button
          onClick={() => {
            // Minted outside the updater: bumping the ref inside it would make
            // the updater impure, and React may call it more than once.
            const key = mintLevelKey();
            setDraft(current => ({
              ...current,
              levels: [...current.levels, emptyLevel(key)],
            }));
          }}
        >
          <FormattedMessage {...messages.addLevel} />
        </Button>

        {/* Said plainly rather than left to be discovered: only a level's
            data-key conditions are edited above (AXHE-3929). Other condition
            types and the rule's own scope conditions are not shown here, and
            an admin who cannot see them has no way to know they survive the
            save. */}
        <Typography variant="body2" color="textSecondary" className={styles.panelNote}>
          <FormattedMessage {...messages.conditionsPreserved} />
        </Typography>

        <div className={styles.panelActions}>
          <Button onClick={onClose}>
            <FormattedMessage {...globalMessages.cancel} />
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={result.status === 'loading'}
          >
            <FormattedMessage {...messages.saveRule} />
          </Button>
        </div>
      </div>
    </>
  );
};

/**
 * The form in its slide-in container.
 *
 * Split from `RuleEditorForm` so the form can be exercised on its own. MUI v4's
 * Drawer renders through a Modal portal, and under jsdom repeated re-renders of
 * the portal's contents detach it from the document — so a test that types more
 * than a character or two into a field loses the whole subtree and every
 * subsequent query fails, for reasons that have nothing to do with the form.
 *
 * The split is worth having regardless: the form owns the draft, the validation
 * and the write, and the drawer is only where it sits.
 */
export const RuleEditorPanel: React.FC<RuleEditorPanelProps> = ({
  open,
  onClose,
  ...formProps
}) => (
  <Drawer anchor="right" open={open} onClose={onClose}>
    <RuleEditorForm onClose={onClose} {...formProps} />
  </Drawer>
);
